import React, { useCallback, useEffect, useState } from 'react';
import useTranslation from '../../../hooks/translation-hook';
import NavigationElements from '../../../models/NavigationElements';
import MyAccountPageWrapper from '../MyAccountPageWrapper';
import SettingsOptions from '../../common/SettingsOptions';
import { useAppDispatch, useAppSelector } from '../../../hooks/store-hook';
import { AuthenticateClient, DeviceTheme } from '../../../api/api.g';
import useMyAccountActions from '../../../hooks/myAccountActions-hook';
import { LoggedState } from '../../../models/AuthModelsCommon';
import { lightBoxActions } from '../../../store/lightBoxSlice';
import ProfileModal from '../myprofile/ProfileModal';
import { Modal, Platform, View } from 'react-native';
import CustomTextInput, { InputTypeValidation } from '../../common/CustomTextInput';
import Label from '../../common/Label';
import { authActions } from '../../../models/helpers/platformImportHelper';
import SettingsDeleteAccount from './SettingsDeleteAccount';
import { useNavigation } from '@react-navigation/native';
import useApi from '../../../hooks/api-hook';
import Loading from '../../common/Loading';
import { rootNavigate } from '../../../navigation/rootNavigation';
import Overlay from '../../common/Overlay';

const Settings = () => {
  const userPrefs = useAppSelector((selector) => selector.userPrefs);
  const myAccountActions = useMyAccountActions();
  const dispatch = useAppDispatch();
  const t = useTranslation();
  const authStore = useAppSelector((selector) => selector.auth);
  const navigation = useNavigation();
  const apiAuth = useApi(AuthenticateClient);

  const [email, setEmail] = useState<string>();
  const [emailError, setEmailError] = useState<string>();
  const [password, setPassword] = useState<string>();
  const [confirmPassword, setConfirmPassword] = useState<string>();
  const [confirmPasswordError, setConfirmPasswordError] = useState<string>();
  const [passwordError, setPasswordError] = useState<string>();
  const [passwordReloginRequired, setPasswordReloginRequired] = useState(false);
  const [loadingEmailChange, setLoadingEmailChange] = useState(false);
  const [isChangeEmailModalOpen, setIsChangeEmailModalOpen] = useState(false);
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false);
  const [mobileSettingsModalType, setMobileSettingsModalType] = useState<'email' | 'password' | 'password-success' | undefined>(undefined);
  const changePasswordReloginMessageKey = 'settings-change-password-relogin-required';
  const isWeb = Platform.OS === 'web';

  const configureCenteredLightBoxPresentation = useCallback(() => {
    dispatch(lightBoxActions.setOverlayVisible(true));
    dispatch(lightBoxActions.setAllowBackgroundInteraction(false));
    dispatch(lightBoxActions.setContentVerticalAlign('center'));
    dispatch(lightBoxActions.setContentHorizontalAlign('center'));
    dispatch(lightBoxActions.setContentTopOffset(0));
    dispatch(lightBoxActions.setContentBottomOffset(0));
    dispatch(lightBoxActions.setContentLeftOffset(0));
  }, [dispatch]);

  const resetChangeEmailState = useCallback(() => {
    setEmail(authStore?.user?.email ?? '');
    setEmailError(undefined);
    setLoadingEmailChange(false);
  }, [authStore?.user?.email]);

  const resetChangePasswordState = useCallback(() => {
    setPassword('');
    setConfirmPassword('');
    setPasswordError(undefined);
    setConfirmPasswordError(undefined);
    setPasswordReloginRequired(false);
  }, []);

  const closeSettingsModal = useCallback(() => {
    setIsChangeEmailModalOpen(false);
    setIsChangePasswordModalOpen(false);
    setMobileSettingsModalType(undefined);
    resetChangeEmailState();
    resetChangePasswordState();

    if (!isWeb) {
      return;
    }

    dispatch(lightBoxActions.setVisibility(false));
    dispatch(lightBoxActions.setContent(<></>));
    configureCenteredLightBoxPresentation();
  }, [
    configureCenteredLightBoxPresentation,
    dispatch,
    isWeb,
    resetChangeEmailState,
    resetChangePasswordState,
  ]);

  const getChangePasswordReloginMessage = () => {
    const translated = t(changePasswordReloginMessageKey);
    if (translated === changePasswordReloginMessageKey) {
      return 'For security reasons, please log in again before trying to change your password.';
    }

    return translated;
  };

  const getErrorCodeOrMessage = (error: any): string => {
    if (!error) {
      return '';
    }

    if (typeof error === 'string') {
      return error;
    }

    return error.code ?? error.message ?? error?.error?.message ?? '';
  };

  const isReloginRequiredPasswordError = (error: any): boolean => {
    const message = `${getErrorCodeOrMessage(error)}`.toUpperCase();
    return message.includes('AUTH/REQUIRES-RECENT-LOGIN') || message.includes('CREDENTIAL_TOO_OLD_LOGIN_AGAIN');
  };

  const handleSetLanguage = () => {
    navigation.navigate(NavigationElements.settingsLanguage as never);
  };

  const handleSetTheme = () => {
    navigation.navigate(NavigationElements.settingsTheme as never);
  };

  const handleSetNotifications = () => {
    navigation.navigate(NavigationElements.settingsNotifications as never);
  };

  const getThemeOptionLabel = () => {
    const preferredTheme = userPrefs.themePreference ?? DeviceTheme.System;
    const isFrench = (userPrefs.uiLanguage ?? '').toLowerCase().startsWith('fr');

    if (preferredTheme === DeviceTheme.System) {
      return isFrench ? 'Système' : 'System';
    }

    if (preferredTheme === DeviceTheme.Dark) {
      return isFrench ? 'Sombre' : 'Dark';
    }

    return isFrench ? 'Clair' : 'Light';
  };

  useEffect(() => {
    if (authStore?.user) {
      setEmail(authStore.user.email);
    }
  }, [authStore?.user?.email]);

  const validateEmail = () => {
    const regexp = new RegExp(
      /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    );
    var isValidEmail: boolean;

    isValidEmail = regexp.test(email);

    if (isValidEmail) {
      setEmailError(undefined);
      return true;
    } else {
      setEmailError(t('Invalid-email-address'));
      return false;
    }
  };

  const validatePassword = () => {
    setPasswordError(undefined);
    setConfirmPasswordError(undefined);

    if (password == '') {
      setPasswordError(t('login.password_required'));
      return false;
    }
    if (confirmPassword == '') {
      setConfirmPasswordError(t('login.password_required'));
      return false;
    }
    if (password && confirmPassword && password != confirmPassword) {
      setConfirmPasswordError(t('passwords_must_match_error'));
      return false;
    }

    return true;
  };

  const getChangeEmailModal = () => {
    return (
      <ProfileModal
        width={340}
        title={t('change_email')}
        hideConfirm={loadingEmailChange}
        onClose={closeSettingsModal}
        onConfirm={() => {
          if (validateEmail()) {
            setLoadingEmailChange(true);
            dispatch(
              authActions.changeEmail({
                email: email,
                callback: (e) => {
                  if (e === true) {
                    closeSettingsModal();
                    setLoadingEmailChange(false);
                    myAccountActions.setVisibility(false);
                    rootNavigate('logout', { changeEmail: true });
                  } else {
                    setLoadingEmailChange(false);
                    if (e === 'auth/email-already-in-use') {
                      setEmailError(t('login.email_in_use'));
                    }
                    if (e === 'auth/requires-recent-login') {
                      setEmailError(t('recent-login-required'));
                    }
                  }
                },
              })
            );
          }
        }}
      >
        <>
          {!loadingEmailChange && (
            <View>
              <Label style={{ marginBottom: 8 }}>{t('new_email')}</Label>
              <CustomTextInput
                inputTypeValidation={InputTypeValidation.email}
                showErrorMessage
                hasError={!!emailError}
                errorMessage={emailError}
                onChangeText={(value) => {
                  setEmail(value);
                  setEmailError(undefined);
                }}
                onBlur={(e) => validateEmail()}
                value={email}
              />
            </View>
          )}
          {loadingEmailChange && <Loading />}
        </>
      </ProfileModal>
    );
  };

  const getChangePasswordModal = () => {
    return (
      <ProfileModal
        width={340}
        title={t('change_password')}
        onClose={closeSettingsModal}
        onConfirm={() => {
          if (passwordReloginRequired) {
            closeSettingsModal();
            return;
          }

          if (validatePassword())
            dispatch(
              authActions.changePassword({
                password: password,
                callback: (e) => {
                  if (e === true) {
                    setPassword('');
                    setConfirmPassword('');
                    setPasswordReloginRequired(false);
                    setPasswordError(undefined);
                    setConfirmPasswordError(undefined);
                    setIsChangePasswordModalOpen(false);

                    if (isWeb) {
                      configureCenteredLightBoxPresentation();
                      dispatch(lightBoxActions.setContent(getPasswordChangedSuccessModal()));
                      dispatch(lightBoxActions.setVisibility(true));
                    } else {
                      setMobileSettingsModalType('password-success');
                    }
                  } else if (isReloginRequiredPasswordError(e)) {
                    setPasswordReloginRequired(true);
                    setPasswordError(undefined);
                    setConfirmPasswordError(undefined);
                  } else if (e === 'auth/weak-password') {
                    setPasswordError(t('login.weak_password'));
                  } else {
                    setPasswordError(e);
                  }
                },
              })
            );
        }}
      >
        <View>
          {!passwordReloginRequired && (
            <>
              <Label style={{ marginBottom: 8 }}>{t('New-password')}</Label>
              <CustomTextInput
                secureTextEntry
                showErrorMessage
                hasError={!!passwordError}
                errorMessage={passwordError}
                onChangeText={(value) => {
                  setPassword(value);
                  setPasswordError(undefined);
                }}
                onBlur={(e) => validatePassword()}
                value={password}
              />
              <Label style={{ marginTop: 16, marginBottom: 8 }}>{t('Confirm-password')}</Label>
              <CustomTextInput
                secureTextEntry
                showErrorMessage
                hasError={!!confirmPasswordError}
                errorMessage={confirmPasswordError}
                onChangeText={(value) => {
                  setConfirmPassword(value);
                  setConfirmPasswordError(undefined);
                }}
                onBlur={(e) => validatePassword()}
                value={confirmPassword}
              />
            </>
          )}
          {passwordReloginRequired && (
            <Label style={{ marginBottom: 8, lineHeight: 22 }} numberOfLines={5}>
              {getChangePasswordReloginMessage()}
            </Label>
          )}
        </View>
      </ProfileModal>
    );
  };

  const getPasswordChangedSuccessModal = () => {
    return (
      <ProfileModal
        width={340}
        title={t('success')}
        onClose={closeSettingsModal}
        onConfirm={closeSettingsModal}
      >
        <View>
          <Label style={{ marginBottom: 8 }} numberOfLines={5}>
            {t('successfully_updated_password')}
          </Label>
        </View>
      </ProfileModal>
    );
  };

  useEffect(() => {
    if (!isWeb || !isChangePasswordModalOpen) {
      return;
    }

    dispatch(lightBoxActions.setContent(getChangePasswordModal()));
  }, [isWeb, isChangePasswordModalOpen, password, confirmPassword, confirmPasswordError, passwordError, passwordReloginRequired]);

  useEffect(() => {
    if (!isWeb || !isChangeEmailModalOpen) {
      return;
    }

    dispatch(lightBoxActions.setContent(getChangeEmailModal()));
  }, [isWeb, isChangeEmailModalOpen, email, emailError, loadingEmailChange]);

  useEffect(() => {
    if (!isWeb) {
      return;
    }

    // Prevent stale map popup presentation state from leaking into settings modals.
    dispatch(lightBoxActions.setVisibility(false));
    dispatch(lightBoxActions.setSecondVisibility(false));
    dispatch(lightBoxActions.setContent(<></>));
    dispatch(lightBoxActions.setSecondContent(<></>));
    configureCenteredLightBoxPresentation();
  }, [dispatch, configureCenteredLightBoxPresentation, isWeb]);

  useEffect(() => {
    return () => {
      closeSettingsModal();
    };
  }, [closeSettingsModal]);

  const changePassword = () => {
    resetChangeEmailState();
    resetChangePasswordState();
    setIsChangeEmailModalOpen(false);
    setIsChangePasswordModalOpen(true);
    if (isWeb) {
      configureCenteredLightBoxPresentation();
      dispatch(lightBoxActions.setContent(getChangePasswordModal()));
      dispatch(lightBoxActions.setVisibility(true));
      return;
    }

    setMobileSettingsModalType('password');
  };

  const changeEmail = () => {
    resetChangePasswordState();
    resetChangeEmailState();
    setIsChangePasswordModalOpen(false);
    setIsChangeEmailModalOpen(true);
    if (isWeb) {
      configureCenteredLightBoxPresentation();
      dispatch(lightBoxActions.setContent(getChangeEmailModal()));
      dispatch(lightBoxActions.setVisibility(true));
      return;
    }

    setMobileSettingsModalType('email');
  };

  return (
    <MyAccountPageWrapper
      navigationTitle={t('my_profile')}
      title={t('settings')}
      navigationAction={NavigationElements.menu}
      titleStyle={{ textTransform: 'none' }}
      footerElement={authStore.logged === LoggedState.Logged ? <SettingsDeleteAccount /> : undefined}
    >
      {!isWeb && (
        <Modal animationType="none" visible={!!mobileSettingsModalType} transparent={true} onRequestClose={closeSettingsModal}>
          <Overlay showOverLay={true} zIndex={5000} onPress={closeSettingsModal}>
            {mobileSettingsModalType === 'password'
              ? getChangePasswordModal()
              : mobileSettingsModalType === 'password-success'
              ? getPasswordChangedSuccessModal()
              : getChangeEmailModal()}
          </Overlay>
        </Modal>
      )}
      {authStore.logged !== LoggedState.Anonymous && <SettingsOptions bottomBorder title={t('email')} option={t('change_email')} onPress={changeEmail} />}
      {authStore.logged !== LoggedState.Anonymous && (
        <SettingsOptions bottomBorder title={t('password')} option={t('change_password')} onPress={changePassword} />
      )}
      <SettingsOptions bottomBorder title={t('language')} option={t('english_french')} onPress={handleSetLanguage} />
      <SettingsOptions bottomBorder title={t('appearance')} option={getThemeOptionLabel()} onPress={handleSetTheme} />
      <SettingsOptions title={t('Notifications')} option={t('Manage-desired-notifications')} onPress={handleSetNotifications} />
      {/* <Pressable
        style={{ paddingVertical: 16 }}
        onPress={() => {
          sharedPopup.showAccountVerificationPopup();
        }}
      >
        <Label style={{ fontSize: 16, color: theme.text.darkTurquoiseTwo, fontWeight: '600' }}>{t('Setup 2FA')}</Label>
      </Pressable> */}
    </MyAccountPageWrapper>
  );
};

export default Settings;
